/* eslint-disable max-len */
import { productDetailsAttributeSelector } from '../selectors/product/productDetailsAttributeSelector';
import getSelectedChoicesWithTag from '../actions/getSkuMap/_getSelectedChoicesWithTag';

// 1. The texture name is always in the format of:
//      choice_internal_name-...-choice_internal_name
//      "-" separator for different choices
// 2. The order of the choices is important (based on when they were created)

// safety_helmet
// no_brim | front
// vented | non_vented
// impactarmorliner_no | impactarmorliner_yes

const safetyHelmetTextureMap = {
  // Texture maps with no armor liner because you cannot hide option with rule
  'safety_helmet-no_brim-vented-no': {
    textureMap: 'Helmet',
    prodId: '48-73-1300',
  },
  'safety_helmet-no_brim-non_vented-no': {
    textureMap: 'Helmet',
    prodId: '48-73-1301',
  },
  'safety_helmet-front-vented-no': {
    textureMap: 'Helmet',
    prodId: '48-73-1320',
  },
  'safety_helmet-front-non_vented-no': {
    textureMap: 'Helmet',
    prodId: '48-73-1321',
  },
  'safety_helmet-no_brim-vented-yes': {
    textureMap: 'Helmet',
    prodId: '48-73-1378',
  },
  'safety_helmet-no_brim-non_vented-yes': {
    textureMap: 'Helmet',
    prodId: '48-73-1379',
  },
  'safety_helmet-front-vented-yes': {
    textureMap: 'Helmet',
    prodId: '48-73-1380',
  },
  'safety_helmet-front-non_vented-yes': {
    textureMap: 'Helmet',
    prodId: '48-73-1381',
  },
};

// hat
// front_brim | full_brim
// _4_point_suspension | _6_point_suspension
// vented | non_vented

const hardHatTextureMap = {
  'hat-front_brim-_4_point_suspension-non_vented': {
    textureMap: 'Helmet',
    prodId: '48-73-1100',
  },
  'hat-full_brim-_4_point_suspension-non_vented': {
    textureMap: 'Helmet',
    prodId: '48-73-1101',
  },
  'hat-front_brim-_4_point_suspension-vented': {
    textureMap: 'Helmet',
    prodId: '48-73-1200',
  },
  'hat-full_brim-_4_point_suspension-vented': {
    textureMap: 'Helmet',
    prodId: '48-73-1201',
  },
  'hat-front_brim-_6_point_suspension-non_vented': {
    textureMap: 'Helmet',
    prodId: '48-73-1120',
  },
  'hat-full_brim-_6_point_suspension-non_vented': {
    textureMap: 'Helmet',
    prodId: '48-73-1121',
  },
  'hat-front_brim-_6_point_suspension-vented': {
    textureMap: 'Helmet',
    prodId: '48-73-1220',
  },
  'hat-full_brim-_6_point_suspension-vented': {
    textureMap: 'Helmet',
    prodId: '48-73-1221',
  },
};

// vest
// mesh | performance | standard | surveyors | breakaway | one_pocket
// class => _2 | _3
const safetyVestTextureMap = {
  'vest-standard-class_2': {
    textureMap: 'Front,Back',
    prodId: '48-73-5022',
    type: 'vest',
  },
  'vest-performance-class_2': {
    textureMap: 'Front,Back',
    prodId: '48-73-5042',
    type: 'vest',
  },
  'vest-surveyors-class_2': {
    textureMap: 'Front,Back',
    prodId: '48-73-5162',
    type: 'vest',
  },
  'vest-mesh-class_2': {
    textureMap: 'Front,Back',
    prodId: '48-73-5112',
    type: 'vest',
  },
  'vest-mesh-class_3': {
    textureMap: 'Front,Back',
    prodId: '48-73-5132',
    type: 'vest',
  },
  'vest-standard-class_3': {
    textureMap: 'Front,Back',
    prodId: '48-73-5141',
    type: 'vest',
  },
  'vest-breakaway-class_2': {
    textureMap: 'Front,Back',
    prodId: '48-73-5121',
    type: 'vest',
  },
  'vest-one_pocket-class_2': {
    textureMap: 'Front,Back',
    prodId: '48-73-2241',
    type: 'vest',
  },
  
};

const textureMapProps = {
  ...hardHatTextureMap,
  ...safetyHelmetTextureMap,
  ...safetyVestTextureMap,
};

const getTextureName = (prodId, activeAccessoriesView, ppeProductType) => {
  if (activeAccessoriesView && ppeProductType !== 'vest') return `${prodId}-acc`;

  return prodId;
};

export const getActiveTexture = (state, ppeProductType) => {
  if (!state) return undefined;

  const {
    configurator: { options, choices },
    designContent: { viewAccessories },
  } = state;

  const selectedChoicesWithTag = getSelectedChoicesWithTag(options, choices, 'lp-texture');
 
  if (selectedChoicesWithTag) {
    const textureNameFromChoices = selectedChoicesWithTag.reduce((acc, choice, key) => {
      if (key === selectedChoicesWithTag.length - 1)
        return `${ppeProductType}-${acc}${choice?.internalName?.toLowerCase()}`;

      return `${acc}${choice.internalName.toLowerCase()}-`;
    }, '');

    const textureBasePath = productDetailsAttributeSelector(state)?.ppeTextureFolder;

    if (
      textureBasePath &&
      textureNameFromChoices &&
      !textureNameFromChoices.includes('undefined')
    ) {
      // sample texture url: https://gcc-ppe-images.s3.amazonaws.com/textures/front-non_vented.glb
      const textureName = getTextureName(
        textureMapProps[textureNameFromChoices]?.prodId,
        viewAccessories,
        ppeProductType
      );
      const activeTextureProps = {
        textureMap: textureMapProps[textureNameFromChoices]?.textureMap,
        optionName: textureMapProps[textureNameFromChoices]?.optionName,
        prodId: textureMapProps[textureNameFromChoices]?.prodId,
        model: `${textureBasePath}/${textureName}.glb`,
        type: textureMapProps[textureNameFromChoices]?.type,
      };
      return activeTextureProps;
    }
  }

  return undefined;
};

export const getActiveColor = (selectedChoices, options) =>
  selectedChoices.reduce((acc, choice) => {
    const optionType = options?.[choice?.optionId]?.optionType;

    if (optionType !== null && optionType === 'Primary Color') {
      const hexColor = choice?.tags.find((tag) => tag.includes('hex'))?.replace('hex:', '');

      return hexColor;
    }

    return acc;
  }, null);

const defaultLogoVestState = {
  front: {
    location: 'front',
    locationId: 'logo1',
    bgRemoval: 0,
    position: 'center',
    size: 'large',
    url: null,
  },
  back: {
    location: 'back',
    locationId: 'logo2',
    bgRemoval: 0,
    position: 'center',
    size: 'large',
    url: null,
  },
};

const defaultLogoState = {
  front: {
    location: 'front',
    locationId: 'logo1',
    bgRemoval: 0,
    position: 'center',
    size: 'large',
    url: null,
  },
  back: {
    location: 'back',
    locationId: 'logo2',
    bgRemoval: 0,
    position: 'center',
    size: 'large',
    url: null,
  },
  right: {
    location: 'right',
    locationId: 'logo3',
    bgRemoval: 0,
    position: 'center',
    size: 'large',
    url: null,
  },
  left: {
    location: 'left',
    locationId: 'logo4',
    bgRemoval: 0,
    position: 'center',
    size: 'large',
    url: null,
  },
};

const lpLogoIdMap = {
  front: 'logo1',
  back: 'logo2',
  right: 'logo3',
  left: 'logo4',
};

const updateActiveLogo = (uploadImagesArr) => {
  const activeLogos = uploadImagesArr?.reduce((acc, item) => {
    let newItem = {};
    if (item?.uploadedImageUrl) {
      newItem = {
        [item?.uploadImageTabName.toLowerCase()]: {
          location: item?.uploadImageTabName.toLowerCase() || 'front',
          locationId: lpLogoIdMap[item?.uploadImageTabName?.toLowerCase()] || 'logo1',
          bgRemoval: item?.toggleRemoveBackground ? '1' : '0',
          position: item?.imagePlacement?.toLowerCase() || 'center',
          size: item?.imageSize?.toLowerCase() || 'large',
          url: item?.uploadedImageUrl || null,
        },
      };
    }

    return { ...acc, ...newItem };
  }, {});

  return activeLogos;
};

export const getActiveLogoGroup = (state, ppeProductType) => {
  if (!state) {
    if (ppeProductType === 'vest') return defaultLogoVestState;

    return defaultLogoState;
  }

  const {
    uploadImages: { uploadImages },
  } = state;

  if (ppeProductType === 'vest') {
    return {
      ...defaultLogoVestState,
      ...updateActiveLogo(uploadImages),
    };
  }

  const newActiveLogoGroup = { ...defaultLogoState, ...updateActiveLogo(uploadImages) };

  return newActiveLogoGroup;
};

export const getPrintUploadsLogoImages = (uploadImages, ppeProductType) => {
  const printUploadImages = uploadImages?.reduce((acc, item) => {
    if (!item?.uploadedImageUrl) return acc;

    const uploadImageId = lpLogoIdMap[item?.uploadImageTabName?.toLowerCase()] || 'logo1';
    const uploadedImageUrl = item?.uploadedImageUrl || '';
    const toggleRemoveBackground = item?.toggleRemoveBackground ? '1' : '0';
    const logoColorize = ppeProductType === 'vest' ? '1' : '0';
    const uploadedImagePosition = item?.uploadedImagePosition?.toLowerCase() || 'center';
    const uploadedImageSize = item?.uploadedImageSize?.toLowerCase() || 'medium';

    const baseUrl = 'http://homedepot.liquifire.com/homedepot?set=view%5B%5D';
    const printLogoType = `&set=showLogo%5B${uploadImageId}%5D,type%5Bprint%5D`;
    const setLogo = `&set=${uploadImageId}%5B${uploadedImageUrl}%5D,${uploadImageId}BGremove%5B${toggleRemoveBackground}%5D,${uploadImageId}Colorize%5B${logoColorize}%5D,${uploadImageId}Pos%5B${uploadedImagePosition}%5D,${uploadImageId}Size%5B${uploadedImageSize}%5D`;
    const callInfo = '&call=url%5Bfile:global.type%5D&sink=format%5Bpng%5D';

    const printUrl = `${baseUrl}${printLogoType}${setLogo}${callInfo}`;

    const newUploadImage = { ...acc, [item?.uploadImageTabName]: printUrl };

    return newUploadImage;
  }, {});

  return printUploadImages;
};

export const getActiveTab = (state) => {
  const {
    uploadImages: { currentTab },
  } = state;

  return currentTab;
};